<template>
  <a-modal
      :visible="visible"
      :title="`分配角色${menu.name ? ' - ' + menu.name : ''}`"
      width="70%"
      :footer="null"
      :maskClosable="false"
      :destroyOnClose="true"
      @cancel="handleCancel"
  >
    <!-- 搜索 + 添加角色 + 批量删除 -->
    <div class="search-bar">
      <a-input
          v-model="searchForm.name"
          placeholder="角色名称"
          allow-clear
          style="width: 180px"
          @keyup.enter.native="searchRoles"
      />
      <a-space style="margin-left: 8px">
        <a-button type="primary" @click="searchRoles">查询</a-button>
        <a-button @click="resetSearch">重置</a-button>
      </a-space>
      <a-button
          type="primary"
          icon="plus"
          @click="openRoleSelector"
          style="margin-left: 8px"
      >
        添加角色
      </a-button>
      <a-button
          icon="delete"
          :disabled="selectedRowKeys.length === 0"
          :loading="batchLoading"
          @click="batchRemoveRoles"
          :style="{ marginLeft: '8px', color: selectedRowKeys.length > 0 ? '#f5222d' : '' }"
      >
        批量删除{{ selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : "" }}
      </a-button>
    </div>

    <!-- 已关联角色列表（外层滚动，展示全部） -->
    <div class="table-scroll">
      <a-table
          bordered
          size="small"
          :dataSource="filteredList"
          :loading="loading"
          :pagination="false"
          :rowKey="(record) => record.uuid"
          :row-selection="{
            selectedRowKeys: selectedRowKeys,
            onChange: onSelectRowChange,
          }"
      >
        <a-table-column key="index" title="序号" align="center" :width="60">
          <template slot-scope="text, record, index">{{ index + 1 }}</template>
        </a-table-column>
        <a-table-column key="name" title="角色名称" data-index="name"/>
        <a-table-column key="code" title="角色编码" data-index="code"/>
        <a-table-column key="remarks" title="描述" data-index="remarks"/>
        <a-table-column key="op" title="操作" align="center" :width="160">
          <template slot-scope="text">
            <a-button
                type="link"
                size="small"
                @click="openUserModal(text)"
            >
              分配用户
            </a-button>
            <a-button
                type="link"
                size="small"
                style="color: #f5222d"
                :loading="removingUuid === text.uuid"
                @click="removeRole(text)"
            >
              删除
            </a-button>
          </template>
        </a-table-column>
      </a-table>
    </div>

    <!-- 添加角色选择弹窗 -->
    <a-modal
        :visible="selectorVisible"
        title="选择角色"
        width="720px"
        :zIndex="1001"
        :destroyOnClose="true"
        @cancel="selectorVisible = false"
    >
      <a-input-search
          v-model="selectorSearch"
          placeholder="角色名称 / 编码"
          allow-clear
          style="margin-bottom: 12px"
      />
      <div class="selector-table-scroll">
      <a-table
          bordered
          size="small"
          :dataSource="selectableList"
          :loading="selectorLoading"
          :pagination="false"
          :rowKey="(record) => record.id"
          :row-selection="{
            selectedRowKeys: selectorSelectedKeys,
            onChange: onSelectorSelectChange,
          }"
      >
        <a-table-column key="name" title="角色名称" data-index="name"/>
        <a-table-column key="code" title="角色编码" data-index="code"/>
        <a-table-column key="remarks" title="描述" data-index="remarks"/>
      </a-table>
      </div>

      <template slot="footer">
        <a-space>
          <a-button @click="selectorVisible = false">取消</a-button>
          <a-button type="primary" :loading="addLoading" @click="confirmAddRoles">确定</a-button>
        </a-space>
      </template>
    </a-modal>

    <!-- 分配用户弹窗（复用 role 模块的 user-modal） -->
    <user-modal :visible.sync="userModalVisible" :role="currentRole"/>
  </a-modal>
</template>

<script>
import {fetchDetail, updateRoles} from "@/api/setting/menu-permission";
import {fetchList as fetchRoleList} from "@/api/setting/role";
import UserModal from "@/views/setting/role/components/user-modal";

export default {
  name: "menuRoleModal",
  components: {UserModal},
  props: {
    // 弹窗显隐，配合 .sync 使用
    visible: {type: Boolean, default: false},
    // 当前操作的菜单（列表行，含 uuid/name）
    menu: {type: Object, default: () => ({})},
  },
  data() {
    return {
      loading: false,
      menuRoleList: [], // 该菜单已关联的角色
      searchForm: {name: ""}, // 搜索输入
      appliedFilter: {name: ""}, // 已应用的过滤条件（点查询后生效）
      removingUuid: "",
      selectedRowKeys: [], // 批量删除选中的角色 uuid
      batchLoading: false,

      // 添加角色选择器
      selectorVisible: false,
      selectorLoading: false,
      selectorSearch: "",
      allRoleList: [], // 所有可选角色
      selectorSelectedKeys: [],
      addLoading: false,

      // 分配用户弹窗
      userModalVisible: false,
      currentRole: {},
    };
  },
  computed: {
    // 按已应用的过滤条件本地过滤已关联角色（展示全部，不分页）
    filteredList() {
      const name = (this.appliedFilter.name || "").trim();
      if (!name) {
        return this.menuRoleList;
      }
      return this.menuRoleList.filter(
          (item) => item.name && item.name.indexOf(name) > -1
      );
    },
    // 选择器里可选的角色（排除已添加的，再按关键字过滤）
    selectableList() {
      const existSet = new Set(this.menuRoleList.map((r) => r.id));
      let list = this.allRoleList.filter((r) => !existSet.has(r.id));
      const kw = (this.selectorSearch || "").trim();
      if (kw) {
        list = list.filter(
            (r) =>
                (r.name && r.name.indexOf(kw) > -1) ||
                (r.code && r.code.indexOf(kw) > -1)
        );
      }
      return list;
    },
  },
  watch: {
    visible(val) {
      // 弹窗打开时加载已关联角色
      if (val && this.menu.uuid) {
        this.removingUuid = "";
        this.selectedRowKeys = [];
        this.getDetail();
      }
      // 关闭时清空状态
      if (!val) {
        this.searchForm = {name: ""};
        this.appliedFilter = {name: ""};
        this.selectedRowKeys = [];
        this.menuRoleList = [];
        this.selectorVisible = false;
        this.userModalVisible = false;
      }
    },
  },
  methods: {
    handleCancel() {
      this.$emit("update:visible", false);
    },

    // 查询：把输入条件应用到过滤
    searchRoles() {
      this.appliedFilter = {...this.searchForm};
    },
    // 重置：清空输入和已应用条件
    resetSearch() {
      this.searchForm = {name: ""};
      this.appliedFilter = {name: ""};
    },
    // 表格多选变化
    onSelectRowChange(keys) {
      this.selectedRowKeys = keys;
    },

    // 查询菜单详情（含关联角色）
    // 接口：GET /user-service/menuPermission/detail/{menuUuid}
    // 假设返回 res.roles 为关联角色数组；若后端字段名不是 roles，
    // 把下面 res.roles 改成对应字段名即可。
    getDetail() {
      this.loading = true;
      fetchDetail(this.menu.uuid)
          .then((res) => {
            console.log("res===",res)
            this.loading = false;
            this.menuRoleList = Array.isArray(res.roles) ? res.roles : [];
          })
          .catch(() => {
            this.loading = false;
            this.menuRoleList = [];
          });
    },

    // 打开添加角色选择器
    openRoleSelector() {
      this.selectorSelectedKeys = [];
      this.selectorSearch = "";
      this.selectorVisible = true;
      this.getRoleList();
    },
    // 获取所有可选角色
    getRoleList() {
      this.selectorLoading = true;
      fetchRoleList({
        pageNum: 1,
        pageSize: 10000,
      })
          .then((res) => {
            this.selectorLoading = false;
            this.allRoleList = Array.isArray(res.list) ? res.list : [];
          })
          .catch(() => {
            this.selectorLoading = false;
          });
    },
    // 选择器多选变化
    onSelectorSelectChange(keys) {
      this.selectorSelectedKeys = keys;
    },
    // 确认添加角色：合并已有后调用 updateRoles
    confirmAddRoles() {
      if (this.selectorSelectedKeys.length === 0) {
        this.$message.warning("请先选择要添加的角色");
        return;
      }
      this.addLoading = true;
      const existingIds = this.menuRoleList.map((r) => r.id);
      const merged = [...new Set([...existingIds, ...this.selectorSelectedKeys])];
      updateRoles(this.menu.uuid, merged)
          .then(() => {
            this.addLoading = false;
            this.selectorVisible = false;
            this.getDetail();
          })
          .catch(() => {
            this.addLoading = false;
          });
    },

    // 从当前菜单删除某个角色：剔除后调用 updateRoles 提交剩余角色列表
    removeRole(text) {
      const that = this;
      this.$confirm({
        title: "确认要将该角色从当前菜单中删除吗？",
        onOk() {
          that.removingUuid = text.uuid;
          const remaining = that.menuRoleList
              .filter((r) => r.uuid !== text.uuid)
              .map((r) => r.id);
          updateRoles(that.menu.uuid, remaining)
              .then(() => {
                that.removingUuid = "";
                that.selectedRowKeys = [];
                that.getDetail();
              })
              .catch(() => {
                that.removingUuid = "";
              });
        },
      });
    },

    // 批量删除：把选中的角色从菜单中剔除，调用 updateRoles 提交剩余角色列表
    batchRemoveRoles() {
      if (this.selectedRowKeys.length === 0) {
        this.$message.warning("请先勾选要删除的角色");
        return;
      }
      const that = this;
      this.$confirm({
        title: `确认要删除选中的 ${this.selectedRowKeys.length} 个角色吗？`,
        onOk() {
          that.batchLoading = true;
          const set = new Set(that.selectedRowKeys);
          const remaining = that.menuRoleList
              .filter((r) => !set.has(r.uuid))
              .map((r) => r.id);
          updateRoles(that.menu.uuid, remaining)
              .then(() => {
                that.batchLoading = false;
                that.selectedRowKeys = [];
                that.getDetail();
              })
              .catch(() => {
                that.batchLoading = false;
              });
        },
      });
    },

    // 打开分配用户弹窗（复用 role 模块的 user-modal）
    openUserModal(text) {
      this.currentRole = text;
      this.userModalVisible = true;
    },
  },
};
</script>

<style lang="less" scoped>
.search-bar {
  display: flex;
  align-items: center;
}

.table-scroll {
  max-height: 420px;
  overflow-y: auto;
  margin-top: 12px;
}

.selector-table-scroll {
  max-height: 400px;
  overflow-y: auto;
}
</style>
